import {Component, Inject, OnInit, Optional} from '@angular/core';
import {FormBuilder, Validators} from '@angular/forms';
import {ActivatedRoute, Router} from '@angular/router';
import {RewardsAdminService} from '../../../../services/rewards-admin/rewards-admin.service';
import {UiAlertService} from '../../../../services/ui-alert/ui-alert.service';
import {MatSnackBar} from '@angular/material/snack-bar';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';

@Component({
  selector: 'app-superadmin-brand-detail',
  templateUrl: './superadmin-brand-detail.component.html',
  styleUrls: ['./superadmin-brand-detail.component.scss'],
})
export class SuperadminBrandDetailComponent implements OnInit {
  public brand: any;
  public saving = false;
  public isDialog = false;

  public form = this.formBuilder.group({
    name: ['', Validators.required],
    accountId: [''],
    config: [{}],
    partnerFilterMode: [{}],
  });

  constructor(
    private formBuilder: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private rewardsAdminService: RewardsAdminService,
    private uiAlertService: UiAlertService,
    private snackBar: MatSnackBar,
    @Optional() private dialogRef?: MatDialogRef<SuperadminBrandDetailComponent>,
    @Optional() @Inject(MAT_DIALOG_DATA) private dialogData?: { brandId?: string; brand?: any },
  ) {}

  ngOnInit(): void {
    this.isDialog = !!this.dialogRef;
    this.loadBrand();
  }

  parseJsonValue(value: any): any {
    if (typeof value === 'string') {
      try {
        return value ? JSON.parse(value) : {};
      } catch {
        return value;
      }
    }
    return value ?? {};
  }

  async loadBrand(): Promise<void> {
    const id = this.dialogData?.brandId || this.getBrandId(this.dialogData?.brand) || this.route.snapshot.paramMap.get('id') || 'new';
    if (this.dialogData?.brand) {
      this.brand = this.normalizeBrandRecord(this.dialogData.brand);
      this.form.patchValue({
        name: this.brand.name,
        accountId: this.brand.accountId,
        config: this.parseJsonValue(this.brand.config),
        partnerFilterMode: this.parseJsonValue(this.brand.partnerFilterMode),
      });
    }
    if (id === 'new') {
      return;
    }
    try {
      this.brand = this.normalizeBrandRecord(await this.rewardsAdminService.getBrandById(id));
      this.form.patchValue({
        name: this.brand.name,
        accountId: this.brand.accountId,
        config: this.parseJsonValue(this.brand.config),
        partnerFilterMode: this.parseJsonValue(this.brand.partnerFilterMode),
      });
    } catch (error) {
      console.error('Error loading brand', error);
    }
  }

  toJsonValue(value: any): any {
    if (typeof value === 'string') {
      try {
        return JSON.parse(value);
      } catch {
        return value;
      }
    }
    return value;
  }

  async save(): Promise<void> {
    if (this.form.invalid) {
      return;
    }
    this.saving = true;
    const payload: any = {
      name: this.form.get('name')?.value,
      accountId: this.form.get('accountId')?.value,
      config: this.toJsonValue(this.form.get('config')?.value),
      partnerFilterMode: this.toJsonValue(this.form.get('partnerFilterMode')?.value),
    };
    try {
      if (this.brand) {
        payload.id = this.getBrandId(this.brand);
        await this.rewardsAdminService.putBrand(payload);
        this.snackBar.open('Brand successfully updated', 'Close', { duration: 2000 });
      } else {
        await this.rewardsAdminService.postBrand(payload);
        this.snackBar.open('Brand successfully created', 'Close', { duration: 2000 });
      }
      if (this.dialogRef) {
        this.dialogRef.close(true);
      } else {
        this.router.navigate(['/superadmin/rewards/brands']).then();
      }
    } catch (error) {
      console.error('Error saving brand', error);
    } finally {
      this.saving = false;
    }
  }

  async delete(): Promise<void> {
    if (!this.brand) {
      return;
    }
    const confirm = await this.uiAlertService.presentAlertConfirm('Delete this brand?');
    if (!confirm) {
      return;
    }
    try {
      await this.rewardsAdminService.deleteBrand(this.brand);
      this.snackBar.open('Brand successfully deleted', 'Close', { duration: 2000 });
      if (this.dialogRef) {
        this.dialogRef.close(true);
      } else {
        this.router.navigate(['/superadmin/rewards/brands']).then();
      }
    } catch (error) {
      console.error('Error deleting brand', error);
    }
  }

  cancel(): void {
    if (this.dialogRef) {
      this.dialogRef.close(false);
    } else {
      this.router.navigate(['/superadmin/rewards/brands']).then();
    }
  }

  private getBrandId(brand: any): string {
    return String(brand?.id || brand?.brandId || brand?._id || '').trim();
  }

  private normalizeBrandRecord(brand: any): any {
    if (!brand) {
      return brand;
    }

    const brandId = this.getBrandId(brand);
    return brandId ? { ...brand, id: brandId } : brand;
  }
}
